import React, { lazy, Suspense } from "react";
import ReactDOM from "react-dom/client";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import App from "./App.jsx";
import ChatApp from "./chat-platform/ChatApp";
import { ChatAssistant } from "./chat-platform";
import HomeDemo from "./components/HomeDemo.jsx";
import HomeNew from "./components/HomeNew.jsx";
import NewHomePage from "./components/nebula/NewHomePage.jsx";
import KitDemo from "./components/KitDemo.jsx";
import ChatAssistantDemo from "./components/ChatAssistantDemo.jsx";
import LoginPage from "./components/LoginPage.jsx";
import RegisterPage from "./components/RegisterPage.jsx";
import ForgotPasswordPage from "./components/ForgotPasswordPage.jsx";
import PrivateRoute from "./components/PrivateRoute.jsx";
import ChatAssistantRoute from "./components/ChatAssistantRoute.jsx";
import TermsPage from "./components/TermsPage.jsx";
import PrivacyPage from "./components/PrivacyPage.jsx";
import {
  AdminLayout,
  AdminDashboard,
  AdminAgents,
  AdminAgentView,
  AdminAgentForm,
  AdminTools,
  AdminSkills,
  AdminSkillForm,
  AdminSubagents,
  AdminSubagentForm,
  AdminKnowledge,
  AdminKnowledgeForm,
  AdminMCP,
  AdminMCPForm,
  AdminSync,
  AdminCredentials,
  AdminSyncConfigs,
  AdminEnvironments,
  AdminApiKeys,
  AdminTokenUsage,
  AdminModelCosts,
  AdminUserTiers,
  AdminSystemConfigs,
  AdminSessions,
  AdminLongMemory,
  AdminContentUrls,
  AdminArtifacts,
  AdminShares,
  AdminCheckpoints,
  AdminUserDetail,
  AdminRAGCollections,
  AdminRAGCollectionForm,
  AdminRAGDocumentForm,
  AdminRAGSearch,
  AdminRAGSettings,
  AdminRAGOverview,
  AdminRAGIndex,
  AdminVectorStores,
  AdminVectorStoreDetail,
  AdminMonitor,
  AdminNodeDetail,
  AdminRedisCache,
  AdminLocalCache,
  AdminSnapshots,
  AdminWSMonitor,
  AdminReminders,
  AdminReminderForm,
  AdminHub,
  AdminSandbox,
  AdminCrawler,
  AdminScheduledTasks,
  AdminScheduledTaskForm,
  AdminSignalOutputs,
} from "./components/admin";
import AdminInsightCategories from "./components/admin/AdminInsightCategories.jsx";
import AdminFeedVisibility from "./components/admin/AdminFeedVisibility.jsx";
import AdminTierWindowLimits from "./components/admin/AdminTierWindowLimits.jsx";
import AdminBudgetAdjustments from "./components/admin/AdminBudgetAdjustments.jsx";
import AdminBudgetUsage from "./components/admin/AdminBudgetUsage.jsx";
import AdminOCR from "./components/admin/ocr/AdminOCR.jsx";
import AdminInstrumentIntelligence from "./components/admin/AdminInstrumentIntelligence.jsx";
import AdminInsightDetail from "./components/admin/AdminInsightDetail.jsx";
import AdminInstrumentProfiles from "./components/admin/AdminInstrumentProfiles.jsx";
import AdminGeminiCaches from "./components/admin/AdminGeminiCaches.jsx";
import AdminGeminiCacheDetail from "./components/admin/AdminGeminiCacheDetail.jsx";
import AdminInstrumentProfileDetail from "./components/admin/AdminInstrumentProfileDetail.jsx";
import AdminInstrumentProfileEditor from "./components/admin/AdminInstrumentProfileEditor.jsx";
import AdminInstrumentIngest from "./components/admin/AdminInstrumentIngest.jsx";
import XchangeLayout from "./xchange-platform/XchangeLayout";
import EkycPage from "./xchange-platform/modules/ekyc";
import ConnectorPage from "./xchange-platform/modules/connector";
import TradingPage from "./xchange-platform/modules/trading";
import OrdersPage from "./xchange-platform/modules/orders";
import PortfolioPage from "./xchange-platform/modules/portfolio";
import "./index.css";
import OnboardingPage from "./chat-platform/modules/portfolio-ai/pages/OnboardingPage.jsx";
import NebulaInitPage from "./chat-platform/modules/portfolio-ai/pages/NebulaInitPage.jsx";
import InsightsPage from "./chat-platform/modules/insights/InsightsPage.jsx";
import InsightDetailPage from "./chat-platform/modules/insights/InsightDetailPage.jsx";
import { Provider as ReduxProvider } from "react-redux";
import { store as chatStore } from "./chat-platform/store";

const PortfolioAiLayout = lazy(
  () => import("./chat-platform/modules/portfolio-ai/PortfolioAiLayout.jsx"),
);
const DashboardPage = lazy(
  () => import("./chat-platform/modules/portfolio-ai/pages/DashboardPage.jsx"),
);
const PortfolioDetailPage = lazy(
  () =>
    import("./chat-platform/modules/portfolio-ai/pages/PortfolioDetailPage.jsx"),
);
const HedgingDetailPage = lazy(
  () =>
    import("./chat-platform/modules/portfolio-ai/pages/HedgingDetailPage.jsx"),
);
const MarginDetailPage = lazy(
  () =>
    import("./chat-platform/modules/portfolio-ai/pages/MarginDetailPage.jsx"),
);
const HistoryPage = lazy(
  () => import("./chat-platform/modules/portfolio-ai/pages/HistoryPage.jsx"),
);
const PerformancePage = lazy(
  () =>
    import("./chat-platform/modules/portfolio-ai/pages/PerformancePage.jsx"),
);
const BacktestPage = lazy(
  () => import("./chat-platform/modules/portfolio-ai/pages/BacktestPage.jsx"),
);
const ChatLayout = lazy(() => import("./chat-platform/ChatLayout"));
const PricingPage = lazy(
  () => import("./chat-platform/modules/pricing/PricingPage.jsx"),
);
const PaymentPage = lazy(
  () => import("./chat-platform/modules/payment/PaymentPage.jsx"),
);
const PaymentConfirmationPage = lazy(
  () => import("./chat-platform/modules/payment/PaymentConfirmationPage.jsx"),
);
const VdscAccountCreation = lazy(
  () => import("./chat-platform/modules/vdsc/VdscAccountCreation.jsx"),
);
const VdscAccountUpdation = lazy(
  () => import("./chat-platform/modules/vdsc/VdscAccountUpdation.jsx"),
);
const VdscSupportLink = lazy(
  () => import("./chat-platform/modules/vdsc/VdscSupportLink.jsx"),
);

const ADMIN_ENABLED = import.meta.env.VITE_ENABLE_ADMIN === "true";
const XCHANGE_ENABLED = import.meta.env.VITE_ENABLE_XCHANGE !== "false";

ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <ReduxProvider store={chatStore}>
      <Router>
        <Routes>
          {/* Login page */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/nebula-init" element={<NebulaInitPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />

          {/* Legal pages */}
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route
            path="/pricing"
            element={
              <Suspense fallback={null}>
                <PricingPage />
              </Suspense>
            }
          />
          <Route
            path="/payment/:id"
            element={
              <Suspense fallback={null}>
                <PaymentPage />
              </Suspense>
            }
          />
          <Route
            path="/payment-confirmation"
            element={
              <Suspense fallback={null}>
                <PaymentConfirmationPage />
              </Suspense>
            }
          />

          {/* Standalone demo home page */}
          <Route path="/home-demo" element={<HomeDemo />} />
          <Route path="/home-new" element={<HomeNew />} />
          <Route path="/nebula" element={<NewHomePage />} />
          <Route path="/kit-demo" element={<KitDemo />} />
          <Route path="/assistant-demo" element={<ChatAssistantDemo />} />

          {/* Admin routes (controlled by VITE_ENABLE_ADMIN env) */}
          {ADMIN_ENABLED && (
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="agents" element={<AdminAgents />} />
              <Route path="agents/new" element={<AdminAgentForm />} />
              <Route path="agents/:agentId" element={<AdminAgentView />} />
              <Route path="agents/:agentId/edit" element={<AdminAgentForm />} />
              <Route path="tools" element={<AdminTools />} />
              <Route path="skills" element={<AdminSkills />} />
              <Route path="skills/new" element={<AdminSkillForm />} />
              <Route path="skills/:skillId" element={<AdminSkillForm />} />
              <Route
                path="skills/:skillId/resources"
                element={<AdminSkillForm />}
              />
              <Route path="skills/:skillId/edit" element={<AdminSkillForm />} />
              <Route
                path="skills/:skillId/edit/resources"
                element={<AdminSkillForm />}
              />
              <Route path="subagents" element={<AdminSubagents />} />
              <Route path="subagents/new" element={<AdminSubagentForm />} />
              <Route
                path="subagents/:agentId"
                element={<AdminSubagentForm />}
              />
              <Route
                path="subagents/:agentId/edit"
                element={<AdminSubagentForm />}
              />
              <Route path="knowledge" element={<AdminKnowledge />} />
              <Route path="knowledge/new" element={<AdminKnowledgeForm />} />
              <Route
                path="knowledge/:knowledgeId"
                element={<AdminKnowledgeForm />}
              />
              <Route
                path="knowledge/:knowledgeId/edit"
                element={<AdminKnowledgeForm />}
              />
              <Route path="reminders" element={<AdminReminders />} />
              <Route path="reminders/new" element={<AdminReminderForm />} />
              <Route path="reminders/:id" element={<AdminReminderForm />} />
              <Route path="mcp" element={<AdminMCP />} />
              <Route path="mcp/new" element={<AdminMCPForm />} />
              <Route path="mcp/:mcpId" element={<AdminMCPForm />} />
              <Route path="mcp/:mcpId/edit" element={<AdminMCPForm />} />
              <Route
                path="rag"
                element={<Navigate to="/admin/rag/overview" replace />}
              />
              <Route path="rag/overview" element={<AdminRAGOverview />} />
              <Route path="rag/search" element={<AdminRAGSearch />} />
              <Route path="rag/index" element={<AdminRAGIndex />} />
              <Route path="rag/manage" element={<AdminRAGCollections />} />
              <Route
                path="rag/manage/new"
                element={<AdminRAGCollectionForm />}
              />
              <Route
                path="rag/manage/:collectionId"
                element={<Navigate to="docs" replace />}
              />
              <Route
                path="rag/manage/:collectionId/docs"
                element={<AdminRAGCollectionForm />}
              />
              <Route
                path="rag/manage/:collectionId/info"
                element={<AdminRAGCollectionForm />}
              />
              <Route
                path="rag/manage/:collectionId/edit"
                element={<AdminRAGCollectionForm />}
              />
              <Route
                path="rag/manage/:collectionId/docs/new"
                element={<AdminRAGDocumentForm />}
              />
              <Route path="rag/settings" element={<AdminRAGSettings />} />
              <Route
                path="rag/docs/:documentId"
                element={<AdminRAGDocumentForm />}
              />
              <Route path="rag/vector-stores" element={<AdminVectorStores />} />
              <Route
                path="rag/vector-stores/:provider"
                element={<AdminVectorStoreDetail />}
              />
              <Route path="sync" element={<AdminSync />} />
              <Route path="credentials" element={<AdminCredentials />} />
              <Route path="sync-configs" element={<AdminSyncConfigs />} />
              <Route path="environments" element={<AdminEnvironments />} />
              <Route path="api-keys" element={<AdminApiKeys />} />
              <Route path="token-usage" element={<AdminTokenUsage />} />
              <Route path="model-costs" element={<AdminModelCosts />} />
              <Route
                path="tier-window-limits"
                element={<AdminTierWindowLimits />}
              />
              <Route
                path="budget-adjustments"
                element={<AdminBudgetAdjustments />}
              />
              <Route path="budget-usage" element={<AdminBudgetUsage />} />
              <Route path="user-tiers" element={<AdminUserTiers />} />
              <Route path="users/:userId" element={<AdminUserDetail />} />
              <Route path="system-configs" element={<AdminSystemConfigs />} />
              <Route path="sessions" element={<AdminSessions />} />
              <Route path="long-memory" element={<AdminLongMemory />} />
              <Route path="content-urls" element={<AdminContentUrls />} />
              <Route path="artifacts" element={<AdminArtifacts />} />
              <Route path="shares" element={<AdminShares />} />
              <Route path="checkpoints" element={<AdminCheckpoints />} />
              <Route path="monitor" element={<AdminMonitor />} />
              <Route path="monitor/ws" element={<AdminWSMonitor />} />
              <Route path="monitor/node/:pid" element={<AdminNodeDetail />} />
              <Route path="monitor/redis" element={<AdminRedisCache />} />
              <Route path="monitor/local-cache" element={<AdminLocalCache />} />
              <Route path="hub" element={<AdminHub />} />
              <Route path="sandbox" element={<AdminSandbox />} />
              <Route path="scheduled-tasks" element={<AdminScheduledTasks />} />
              <Route
                path="scheduled-tasks/new"
                element={<AdminScheduledTaskForm />}
              />
              <Route
                path="scheduled-tasks/:taskId/edit"
                element={<AdminScheduledTaskForm />}
              />
              <Route
                path="insight-categories"
                element={<AdminInsightCategories />}
              />
              <Route path="feed-visibility" element={<AdminFeedVisibility />} />
              <Route path="signal-outputs" element={<AdminSignalOutputs />} />
              <Route
                path="instrument-intelligence"
                element={<AdminInstrumentIntelligence />}
              />
              <Route
                path="instrument-intelligence/ingest"
                element={<AdminInstrumentIngest />}
              />
              <Route
                path="instrument-intelligence/insights/:insightId"
                element={<AdminInsightDetail />}
              />
              <Route
                path="instrument-profiles"
                element={<AdminInstrumentProfiles />}
              />
              <Route
                path="instrument-profiles/new"
                element={<AdminInstrumentProfileEditor />}
              />
              <Route
                path="instrument-profiles/:canonicalId"
                element={<AdminInstrumentProfileDetail />}
              />
              <Route
                path="instrument-profiles/:canonicalId/edit"
                element={<AdminInstrumentProfileEditor />}
              />
              <Route path="gemini-caches" element={<AdminGeminiCaches />} />
              <Route
                path="gemini-caches/detail"
                element={<AdminGeminiCacheDetail />}
              />
              <Route path="crawler" element={<AdminCrawler />} />
              <Route path="ocr" element={<AdminOCR />} />
              <Route path="snapshots" element={<AdminSnapshots />} />
            </Route>
          )}

          {XCHANGE_ENABLED && (
            <Route path="/xchange" element={<XchangeLayout />}>
              <Route path="ekyc" element={<EkycPage />} />
              <Route path="connector" element={<ConnectorPage />} />
              <Route
                path="connector/:providerCode"
                element={<ConnectorPage />}
              />
              <Route
                path="connector/:providerCode/:step"
                element={<ConnectorPage />}
              />
              <Route path="trading" element={<TradingPage />} />
              <Route path="orders" element={<OrdersPage />} />
              <Route path="portfolio" element={<PortfolioPage />} />
            </Route>
          )}

          <Route path="/" element={<App />}>
            <Route element={<ChatAssistantRoute />}>
              <Route index element={<ChatAssistant embedded={false} />} />
            </Route>

            <Route
              element={
                <Suspense fallback={null}>
                  <ChatLayout />
                </Suspense>
              }
            >
              <Route path="chat" element={<ChatAssistant embedded={false} />} />
              <Route
                path="chat/:sessionId"
                element={<ChatAssistant embedded={false} />}
              />
              <Route
                path="portfolio-ai"
                element={
                  <Suspense fallback={null}>
                    <PortfolioAiLayout />
                  </Suspense>
                }
              >
                <Route
                  index
                  element={
                    <Suspense fallback={null}>
                      <DashboardPage />
                    </Suspense>
                  }
                />
                <Route
                  path="detail"
                  element={
                    <Suspense fallback={null}>
                      <PortfolioDetailPage />
                    </Suspense>
                  }
                />
                <Route
                  path="hedging-detail"
                  element={
                    <Suspense fallback={null}>
                      <HedgingDetailPage />
                    </Suspense>
                  }
                />
                <Route
                  path="margin-detail"
                  element={
                    <Suspense fallback={null}>
                      <MarginDetailPage />
                    </Suspense>
                  }
                />
                <Route
                  path="history"
                  element={
                    <Suspense fallback={null}>
                      <HistoryPage />
                    </Suspense>
                  }
                />
                <Route
                  path="performance"
                  element={
                    <Suspense fallback={null}>
                      <PerformancePage />
                    </Suspense>
                  }
                />
                <Route
                  path="backtest"
                  element={
                    <Suspense fallback={null}>
                      <BacktestPage />
                    </Suspense>
                  }
                />
              </Route>
              <Route
                path="vdsc/create"
                element={
                  <Suspense fallback={null}>
                    <VdscAccountCreation />
                  </Suspense>
                }
              />
              <Route
                path="vdsc/update"
                element={
                  <Suspense fallback={null}>
                    <VdscAccountUpdation />
                  </Suspense>
                }
              />
              <Route
                path="vdsc/link"
                element={
                  <Suspense fallback={null}>
                    <VdscSupportLink />
                  </Suspense>
                }
              />
              <Route
                path="insights"
                element={
                  <PrivateRoute>
                    <InsightsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="insights/:insightId"
                element={
                  <PrivateRoute>
                    <InsightDetailPage />
                  </PrivateRoute>
                }
              />
              {/* Route for shared chats now also uses ChatApp component */}
              <Route path="share/:shareId" element={<ChatApp />} />
              <Route
                path="admin-view/:adminViewSessionId"
                element={<ChatApp />}
              />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </Router>
    </ReduxProvider>
  </React.StrictMode>,
);
